import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  FormControl,
  Autocomplete,
  TextField,
  useMediaQuery,
  Alert,
  Box,
  MenuItem,
  Select,
  InputLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { toast } from "react-toastify";
import { TextareaAutosize } from "@mui/base";

import { useUtil } from "../../../contexts/UtilContext";
import { useAuth } from "../../../contexts/AuthContext";

import LicenseCard from "../LiscenseCard/LicenseCard.jsx";
import AgencyLicenseCard from "./AgencyLicenseCard.jsx";

import styles from "./DashProLicenses.module.css";

function DashProLicenses({ isAgencyOnboard, navigateToAgencies }) {
  const { showLoader, hideLoader } = useUtil();
  const { accessToken } = useAuth();

  // ---------- Professional license state ----------
  const [brokerLicenseNumber, setBrokerLicenseNumber] = useState("");
  const [realtorLicenseNumber, setRealtorLicenseNumber] = useState("");
  const [brokerLicenseVerifiedAt, setBrokerLicenseVerifiedAt] = useState("");
  const [realtorLicenseVerifiedAt, setRealtorLicenseVerifiedAt] = useState("");

  const [licenses, setLicenses] = useState([]); // professional licenses list
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalDescription, setModalDescription] = useState("");
  const [isEditingBroker, setIsEditingBroker] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [licenseNumber, setLicenseNumber] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [licenseType, setLicenseType] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [error, setError] = useState("");

  const [originalLicenseDetails, setOriginalLicenseDetails] = useState(null);
  const [openEdiModal, setEditModal] = useState(false);

  const dropRef = useRef();

  // ---------- Agencies + agency licenses ----------
  const [agencies, setAgencies] = useState([]); // [{id, agency_name}]
  const [agencyLicensesById, setAgencyLicensesById] = useState({}); // { [agencyId]: License[] }
  const [agencyLoadError, setAgencyLoadError] = useState("");

  // ---------- Agency license EDIT modal ----------
  const [agencyEditOpen, setAgencyEditOpen] = useState(false);
  const [agencyEditFile, setAgencyEditFile] = useState(null);
  const [agencyEditForm, setAgencyEditForm] = useState({
    id: null,
    agency_id: null,
    license_type: "",
    license_number: "",
    issuing_state: "",
    issued_date: "",
    expiry_date: "",
    status: "Active",
    license_document_url: "",
  });

  // ---------- Agency license ADD modal (bulk upload) ----------
  const makeAgencyRow = () => ({
    licenseNumber: "",
    licenseType: "",
    issuingState: "", // IMPORTANT: always string (''), never undefined
    issuedDate: "",
    expiryDate: "",
    status: "Active",
    file: null,
    fileName: "",
  });

  const [agencyAddOpen, setAgencyAddOpen] = useState(false);
  const [agencyAddAgencyId, setAgencyAddAgencyId] = useState("");
  const [agencyAddRows, setAgencyAddRows] = useState([makeAgencyRow()]);

  // ---------- UI helpers ----------
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ---------- Constants ----------
  const states = [
    "AL",
    "AK",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "FL",
    "GA",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PA",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
  ];

  const ALLOWED_LICENSE_STATUS = [
    "Active",
    "Expired",
    "Suspended",
    "Under Review",
  ];

  function validateAgencyLicenseRows(rows) {
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.licenseNumber || !r.licenseNumber.trim()) {
        return `Row ${i + 1}: license number is required`;
      }
      if (!r.issuingState || !r.issuingState.trim()) {
        return `Row ${i + 1}: issuing state is required`;
      }
      if (!ALLOWED_LICENSE_STATUS.includes(r.status || "")) {
        return `Row ${
          i + 1
        }: invalid status (must be ${ALLOWED_LICENSE_STATUS.join(", ")})`;
      }
      if (
        r.issuedDate &&
        r.expiryDate &&
        new Date(r.expiryDate) < new Date(r.issuedDate)
      ) {
        return `Row ${i + 1}: expiry date cannot be before issued date`;
      }
    }
    return null;
  }

  // =========================================================
  // Professional license handlers
  // =========================================================
  const openModal = (isBroker, isEdit) => {
    setIsEditingBroker(isBroker);
    setModalTitle(isBroker ? "Broker License" : "Realtor License");
    setModalDescription(
      isEdit ? "Edit your license number" : "Add a new license number"
    );
    setLicenseNumber(
      isEdit ? (isBroker ? brokerLicenseNumber : realtorLicenseNumber) : ""
    );
    setIsEditing(isEdit);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setError("");
    setSelectedState("");
    setLicenseNumber("");
    setImageFile(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) setImageFile(file);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) setImageFile(file);
  };
  const handleDragOver = (e) => e.preventDefault();

  const handleEditLicense = (license) => {
    setIsEditing(true);
    setIsEditingBroker(license.license_type === "broker");
    setModalTitle(
      license.license_type === "broker"
        ? "Edit Broker License"
        : "Edit Realtor License"
    );
    setModalDescription("Update your license information");
    setLicenseNumber(license.license_number);
    setSelectedState(license.state);
    setLicenseType(license.license_type);
    setOriginalLicenseDetails({
      license_number: license.license_number,
      state: license.state,
      license_type: license.license_type,
    });
    setImageFile(null);
    openModal(license.license_type === "broker", true);
  };

  const handleSaveLicense = async () => {
    try {
      const formData = new FormData();
      formData.append("licenseNumber", licenseNumber);
      formData.append("state", selectedState);
      formData.append("licenseType", licenseType);
      formData.append(
        "originalLicenseNumber",
        originalLicenseDetails.license_number
      );
      formData.append("originalState", originalLicenseDetails.state);
      formData.append(
        "originalLicenseType",
        originalLicenseDetails.license_type
      );
      if (imageFile) formData.append("image", imageFile);

      await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/professional/professional-license`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            // your /professional endpoint expects raw token:
            Authorization: accessToken,
          },
        }
      );

      toast.success("License updated successfully");
      setEditModal(false);
      resetFormState();
      await fetchLicenses();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update license");
    }
  };

  const handleDeleteLicense = async (license) => {
    try {
      await axios.delete(
        `${import.meta.env.VITE_BACKEND_URL}/professional/professional-license`,
        {
          headers: { Authorization: accessToken }, // raw token
          data: {
            license_number: license.license_number,
            state: license.state,
            license_type: license.license_type,
          },
        }
      );

      toast.success("Deleted license");
      setLicenses((prev) =>
        prev.filter(
          (l) =>
            !(
              l.license_number === license.license_number &&
              l.state === license.state &&
              l.license_type === license.license_type
            )
        )
      );
      setEditModal(false);
    } catch (err) {
      await fetchLicenses();
      console.error(err);
      toast.error("Failed to delete license");
    }
  };

  const validateLicenseNumber = (value) => {
    const licenseRegex = /^[A-Z]{0,3}[0-9]{5,10}$/;
    if (!value) return { valid: false, message: "License number is required." };
    if (licenseRegex.test(value)) return { valid: true, message: "" };
    return {
      valid: false,
      message:
        "Invalid license number format. Use 5–10 digits, optionally prefixed by up to 3 uppercase letters.",
    };
  };

  const uploadImages = async (
    state,
    licenseNumber,
    licenseType,
    liscenceImage,
    token
  ) => {
    try {
      const formData = new FormData();
      formData.append("image", liscenceImage);
      formData.append("state", state);
      formData.append("licenseNumber", licenseNumber);
      formData.append("licenseType", licenseType);

      const response = await axios.post(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/professional/upload-liscence-images`,
        formData,
        {
          headers: {
            Authorization: token, // raw token
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data?.success) {
        toast.success("Successfully uploaded the license image");
        closeModal();
        return true;
      }
      toast.error(
        response.data?.message || "Some error occurred while uploading images!"
      );
      return false;
    } catch (error) {
      console.error("Error during upload:", error);
      toast.error("Error uploading images. Please try again later.");
      return false;
    }
  };

  const saveLicense = async () => {
    const validation = validateLicenseNumber(licenseNumber);
    const liscenceSelectedState = selectedState;
    const liscenceImage = imageFile;
    const liscenceType = isEditingBroker ? "broker" : "realtor";

    if (!validation.valid) {
      setError(validation.message);
      return;
    }

    setLicenseNumber(licenseNumber);
    setSelectedState(selectedState);
    setLicenseType(liscenceType);

    if (isEditing) {
      await handleSaveLicense();
      closeModal();
      return;
    }

    try {
      showLoader();
      const requestData = isEditingBroker
        ? { brokerLicenseNumber: licenseNumber }
        : { realtorLicenseNumber: licenseNumber };

      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-license/save`,
        requestData,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (response.status === 200 && response.data && response.data.success) {
        toast.success("Successfully saved the license number");

        const isUploadSuccess = await uploadImages(
          liscenceSelectedState,
          licenseNumber,
          liscenceType,
          liscenceImage,
          accessToken
        );
        if (!isUploadSuccess) toast.error("Failed to upload images!");

        if (isEditingBroker) setBrokerLicenseNumber(licenseNumber);
        else setRealtorLicenseNumber(licenseNumber);

        await fetchLicenses();
        setEditModal(false);

        if (isAgencyOnboard) {
          toast.error("redirect to agency onboard");
        }
      } else {
        toast.error(response.data.message || "Failed to save the license.");
      }
    } catch (error) {
      toast.error("Error while saving the license");
    } finally {
      hideLoader();
      closeModal();
      resetFormState();
    }
  };

  // =========================================================
  // Agency license EDIT handlers
  // =========================================================
  const openAgencyEdit = (agencyId, license) => {
    setAgencyEditForm({
      id: license.id,
      agency_id: agencyId,
      license_type: license.license_type || "",
      license_number: license.license_number || "",
      issuing_state: license.issuing_state || "",
      issued_date: license.issued_date ? license.issued_date.slice(0, 10) : "",
      expiry_date: license.expiry_date ? license.expiry_date.slice(0, 10) : "",
      status: license.status || "Active",
      license_document_url: license.license_document_url || "",
    });
    setAgencyEditFile(null);
    setAgencyEditOpen(true);
  };
  const closeAgencyEdit = () => {
    setAgencyEditOpen(false);
    setAgencyEditFile(null);
  };
  const onAgencyEditChange = (e) => {
    const { name, value } = e.target;
    setAgencyEditForm((p) => ({ ...p, [name]: value }));
  };
  const onAgencyEditFile = (e) => {
    const f = e.target.files?.[0];
    setAgencyEditFile(f || null);
  };

  const saveAgencyLicense = async () => {
    const id = agencyEditForm.id;
    if (!id) return;

    try {
      showLoader();

      // 1) update JSON fields
      await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/dash/agency-licenses/${id}`,
        {
          license_type: agencyEditForm.license_type || null,
          license_number: agencyEditForm.license_number || null,
          issuing_state: agencyEditForm.issuing_state || null,
          issued_date: agencyEditForm.issued_date || null,
          expiry_date: agencyEditForm.expiry_date || null,
          status: agencyEditForm.status || "Active",
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      // 2) optional document replace
      if (agencyEditFile) {
        const fd = new FormData();
        fd.append("licenseDocument", agencyEditFile);
        await axios.post(
          `${
            import.meta.env.VITE_BACKEND_URL
          }/dash/agency-licenses/${id}/upload-document`,
          fd,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      }

      toast.success("License updated");
      setAgencyEditOpen(false);

      if (agencyEditForm.agency_id) {
        await fetchAgencyLicenses(agencyEditForm.agency_id);
      } else {
        for (const a of agencies) await fetchAgencyLicenses(a.id);
      }
    } catch (err) {
      console.error("Update agency license failed:", err);
      toast.error("Failed to update license");
    } finally {
      hideLoader();
    }
  };

  const deleteAgencyLicense = async (agencyId, licenseId) => {
    try {
      showLoader();
      await axios.delete(
        `${import.meta.env.VITE_BACKEND_URL}/dash/agency-licenses/${licenseId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toast.success("License deleted");
      await fetchAgencyLicenses(agencyId);
    } catch (err) {
      console.error("Delete agency license failed:", err);
      toast.error("Failed to delete license");
    } finally {
      hideLoader();
    }
  };

  // =========================================================
  // Agency license ADD handlers (bulk upload)
  // =========================================================
  const openAgencyAdd = () => {
    setAgencyAddRows([makeAgencyRow()]);
    setAgencyAddAgencyId("");
    setAgencyAddOpen(true);
  };
  const closeAgencyAdd = () => setAgencyAddOpen(false);

  const addAgencyRow = () => setAgencyAddRows((p) => [...p, makeAgencyRow()]);

  const removeAgencyRow = (idx) =>
    setAgencyAddRows((p) => p.filter((_, i) => i !== idx));

  const onAgencyRowChange = (idx, e) => {
    const { name, value } = e.target;
    setAgencyAddRows((p) => {
      const copy = [...p];
      const row = { ...copy[idx] };
      // Always keep keys defined as strings (never undefined)
      if (name === "issuingState" || name === "status") {
        row[name] = value ?? "";
      } else {
        row[name] = value;
      }
      copy[idx] = row;
      return copy;
    });
  };

  const onAgencyRowFile = (idx, e) => {
    const f = e.target.files?.[0];
    setAgencyAddRows((p) => {
      const copy = [...p];
      copy[idx] = { ...copy[idx], file: f || null, fileName: f?.name || "" };
      return copy;
    });
  };

  const submitAgencyLicensesUpload = async () => {
    if (!agencyAddAgencyId) {
      toast.error("Please select an agency.");
      return;
    }

    try {
      showLoader();

      const validationError = validateAgencyLicenseRows(agencyAddRows);
      if (validationError) {
        toast.error(validationError);
        return;
      }

      const fd = new FormData();
      fd.append("agencyId", String(agencyAddAgencyId));

      // Send ALL rows; backend can ignore those without files if it wants
      const details = agencyAddRows.map((r) => ({
        licenseNumber: r.licenseNumber || null,
        licenseType: r.licenseType || null,
        issuingState: r.issuingState || "", // keep as '' instead of undefined
        issuedDate: r.issuedDate || null,
        expiryDate: r.expiryDate || null,
        status: r.status || "Active",
        hasFile: !!r.file, // in case your backend uses this
      }));
      fd.append("licenseDetails", JSON.stringify(details));

      // Append only files, in the order of rows that have files
      agencyAddRows.forEach((r) => {
        if (r.file) fd.append("files", r.file); // must match multer field: "files"
      });

      await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/dash/agency-licenses/upload`,
        fd,
        { headers: { Authorization: `Bearer ${accessToken}` } } // let Axios set boundary
      );

      toast.success("Agency license(s) uploaded");
      setAgencyAddOpen(false);
      await fetchAgencyLicenses(agencyAddAgencyId);
    } catch (err) {
      console.error("Upload agency licenses failed:", err);
      toast.error("Failed to upload agency license(s).");
    } finally {
      hideLoader();
    }
  };

  // =========================================================
  // Data fetchers
  // =========================================================
  const fetchProLicenses = async (token) => {
    try {
      showLoader();
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-license/list`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200 && response.data?.success) {
        setBrokerLicenseNumber(response.data.brokerLicense);
        setRealtorLicenseNumber(response.data.realtorLicense);
        setBrokerLicenseVerifiedAt(response.data.brokerLicenseVerifiedAt);
        setRealtorLicenseVerifiedAt(response.data.realtorLicenseVerifiedAt);
      } else {
        toast.error(response.data?.message || "Failed to fetch licenses.");
      }
    } catch {
      toast.error("Error while fetching the licenses");
    } finally {
      hideLoader();
    }
  };

  const fetchLicenses = async () => {
    const start = performance.now();
    try {
      const response = await axios.get(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/professional/professional-licenses`,
        { headers: { Authorization: accessToken } } // raw token for this API
      );
      const end = performance.now();
      console.log(`fetchLicenses took ${(end - start).toFixed(2)}ms`);
      if (response.data?.success) setLicenses(response.data.plans);
    } catch (err) {
      console.error("Failed to fetch licenses", err);
    }
  };

  const fetchMyAgencies = async () => {
    try {
      const res = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/dash/my-agencies`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (res.data?.success && Array.isArray(res.data.agencies)) {
        setAgencies(res.data.agencies);
      } else {
        setAgencies([]);
      }
    } catch (e) {
      console.error("Failed to fetch agencies", e);
      setAgencies([]);
      setAgencyLoadError("Could not load your agencies.");
    }
  };

  const fetchAgencyLicenses = async (agencyId) => {
    try {
      const res = await axios.get(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/dash/agency-licenses/by-agency/${agencyId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (res.data?.success && Array.isArray(res.data.licenses)) {
        setAgencyLicensesById((prev) => ({
          ...prev,
          [agencyId]: res.data.licenses,
        }));
      } else {
        setAgencyLicensesById((prev) => ({ ...prev, [agencyId]: [] }));
      }
    } catch (e) {
      console.error(`Failed to fetch licenses for agency ${agencyId}`, e);
      setAgencyLicensesById((prev) => ({ ...prev, [agencyId]: [] }));
    }
  };

  // =========================================================
  // Effects
  // =========================================================
  useEffect(() => {
    if (isModalOpen) {
      setSelectedState("");
      setLicenseNumber("");
      setImageFile(null);
    }
    if (accessToken) {
      fetchLicenses();
      fetchProLicenses(accessToken);
      fetchMyAgencies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    (async () => {
      for (const a of agencies) {
        await fetchAgencyLicenses(a.id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencies]);

  // =========================================================
  // Misc helpers
  // =========================================================
  const resetFormState = () => {
    setSelectedState("");
    setLicenseNumber("");
    setImageFile(null);
  };

  const handleExploreAgency = () => navigateToAgencies();
  const handleAddLicense = (type) => {
    if (type === "broker") openModal(true, false);
    if (type === "realtor") openModal(false, false);
  };
  const hasVerifiedBrokerLicense = licenses.some(
    (license) => license.license_type === "broker" && license.is_verified
  );
  if (!Array.isArray(licenses)) return null;

  // =========================================================
  // Render
  // =========================================================
  return (
    <div className={styles.container}>
      <h1 style={{ marginBottom: "3rem", marginTop: "3rem" }}>
        You can view your professional licenses here
      </h1>

      <div className={`${styles.formComponent}`}>
        <Alert severity="warning" style={{ padding: "10px" }}>
          If you don't provide your broker or realtor license numbers, you won't
          be shown as a certified professional on the website!
        </Alert>
      </div>

      {/* Header + Add Agency License button */}
      <div className={styles.agencyHeader}>
        <h2 className={styles.agencyTitle}>Agency Licenses</h2>
        <Button
          variant="contained"
          size="small"
          className={styles.addAgencyBtn}
          onClick={openAgencyAdd}
        >
          + Add Agency License
        </Button>
      </div>

      {agencyLoadError && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {agencyLoadError}
        </Alert>
      )}

      {/* Agencies list */}
      {agencies.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          You are not associated with any agencies yet.
        </Typography>
      ) : (
        agencies.map((agency) => {
          const list = agencyLicensesById[agency.id] || [];
          return (
            <div key={agency.id} className={styles.licenseSection}>
              <div className={styles.licenseTitle}>
                {agency.agency_name || `Agency #${agency.id}`}
              </div>
              <div className={styles.licenseCardsContainer}>
                {list.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No licenses found for this agency.
                  </Typography>
                ) : (
                  list.map((license) => (
                    <AgencyLicenseCard
                      key={license.id}
                      license={license}
                      onEdit={() => openAgencyEdit(agency.id, license)}
                      onDelete={() =>
                        deleteAgencyLicense(agency.id, license.id)
                      }
                      editable
                    />
                  ))
                )}
              </div>
            </div>
          );
        })
      )}

      {/* Professional licenses */}
      {["broker", "realtor"].map((type) => {
        const filtered = licenses.filter((l) => l.license_type === type);
        return (
          <div key={type} className={styles.licenseSection}>
            <div className={styles.licenseTitle}>
              {type === "broker" ? "Broker Licenses" : "Realtor Licenses"}
            </div>
            <div className={styles.licenseCardsContainer}>
              {filtered.map((license) => (
                <LicenseCard
                  key={
                    license.id ||
                    `${license.license_number}-${license.state}-${license.license_type}`
                  }
                  license={license}
                  onEdit={() => handleEditLicense(license)}
                  onDelete={() => handleDeleteLicense(license)}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Add buttons for professional licenses */}
      <div className={styles.addCardWrapper}>
        <Button
          variant="outlined"
          color="primary"
          className={styles.licenseAddButton}
          onClick={() => handleAddLicense("broker")}
        >
          + Add Another Broker License
        </Button>
      </div>
      <div className={styles.addCardWrapper}>
        <Button
          variant="outlined"
          color="primary"
          className={styles.licenseAddButton}
          onClick={() => handleAddLicense("realtor")}
        >
          + Add Another Realtor License
        </Button>
      </div>

      {hasVerifiedBrokerLicense && (
        <div className={styles.agencyBox}>
          <div className={styles.agnecyMessage}>
            You are now a licensed broker with FirstLand and can bring your
            agency with you!
          </div>
          <div className={styles.agencyButtonContainer}>
            <Button
              variant="contained"
              size="large"
              className={styles.licenseAddButton}
              onClick={handleExploreAgency}
            >
              Explore
            </Button>
          </div>
        </div>
      )}

      {/* Modal: Add/Edit Professional License */}
      <Dialog
        open={isModalOpen}
        onClose={closeModal}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">{modalTitle}</DialogTitle>
        <DialogContent>
          <Typography>{modalDescription}</Typography>

          <FormControl fullWidth margin="normal">
            <Autocomplete
              options={states}
              // allow clearing; use null when empty (no disableClearable)
              value={selectedState ? selectedState : null}
              onChange={(e, newValue) => setSelectedState(newValue || "")}
              renderInput={(params) => (
                <TextField {...params} label="State" variant="outlined" />
              )}
              size={isMobile ? "small" : "medium"}
            />
          </FormControl>

          <TextareaAutosize
            minRows={3}
            maxRows={5}
            placeholder="Enter your license number"
            value={licenseNumber}
            onChange={(e) => setLicenseNumber(e.target.value)}
            className={styles.textareaInput}
          />

          <Box
            ref={dropRef}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            sx={{
              mt: 2,
              border: "2px dashed #1e40af",
              borderRadius: "8px",
              padding: "16px",
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: "#f9f9f9",
            }}
          >
            <Typography variant="body2" color="textSecondary">
              Drag & drop your license image here or click to upload
            </Typography>

            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              id="license-upload"
              onChange={handleFileSelect}
            />
            <label
              htmlFor="license-upload"
              style={{ display: "inline-block", marginTop: "8px" }}
            >
              <Button variant="outlined" component="span">
                Choose File
              </Button>
            </label>

            {imageFile && (
              <Box
                mt={2}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2" color="success.main">
                  Selected: {imageFile.name}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => setImageFile(null)}
                  sx={{ ml: 2 }}
                >
                  Remove
                </Button>
              </Box>
            )}
          </Box>

          {error && (
            <Typography color="error" mt={2}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => saveLicense(imageFile)}
            color="primary"
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal: Edit Agency License (with State dropdown; safe empty-string fallbacks) */}
      <Dialog
        open={agencyEditOpen}
        onClose={closeAgencyEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Agency License</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="License Type"
              name="license_type"
              value={agencyEditForm.license_type ?? ""}
              onChange={onAgencyEditChange}
              fullWidth
            />
            <TextField
              label="License Number"
              name="license_number"
              value={agencyEditForm.license_number ?? ""}
              onChange={onAgencyEditChange}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel id="edit-issuing-state-label">State</InputLabel>
              <Select
                labelId="edit-issuing-state-label"
                label="State"
                name="issuing_state"
                value={agencyEditForm.issuing_state ?? ""} // <- never undefined
                onChange={onAgencyEditChange}
              >
                {states.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="edit-status-label">Status</InputLabel>
              <Select
                labelId="edit-status-label"
                label="Status"
                name="status"
                value={agencyEditForm.status ?? "Active"} // <- never undefined
                onChange={onAgencyEditChange}
              >
                {ALLOWED_LICENSE_STATUS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Issued Date"
              name="issued_date"
              type="date"
              value={agencyEditForm.issued_date ?? ""}
              onChange={onAgencyEditChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Expiry Date"
              name="expiry_date"
              type="date"
              value={agencyEditForm.expiry_date ?? ""}
              onChange={onAgencyEditChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Replace Document (optional)
            </Typography>
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={onAgencyEditFile}
            />
            {agencyEditFile && (
              <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                Selected: {agencyEditFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAgencyEdit}>Cancel</Button>
          <Button variant="contained" onClick={saveAgencyLicense}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal: Add Agency Licenses (bulk upload) */}
      <Dialog
        open={agencyAddOpen}
        onClose={closeAgencyAdd}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Agency License(s)</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="agency-pick-label">Agency</InputLabel>
              <Select
                labelId="agency-pick-label"
                label="Agency"
                value={agencyAddAgencyId || ""} // <- never undefined
                onChange={(e) => setAgencyAddAgencyId(e.target.value || "")}
              >
                {agencies.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.agency_name || `Agency #${a.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {agencyAddRows.map((r, idx) => (
              <Box
                key={idx}
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <strong>License #{idx + 1}</strong>
                  {agencyAddRows.length > 1 && (
                    <Button
                      color="error"
                      variant="text"
                      onClick={() => removeAgencyRow(idx)}
                    >
                      Remove
                    </Button>
                  )}
                </Box>

                <Box className={styles.componentType2}>
                  <TextField
                    label="License Number"
                    name="licenseNumber"
                    value={r.licenseNumber ?? ""} // <- never undefined
                    onChange={(e) => onAgencyRowChange(idx, e)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="License Type (e.g., Brokerage)"
                    name="licenseType"
                    value={r.licenseType ?? ""} // <- never undefined
                    onChange={(e) => onAgencyRowChange(idx, e)}
                    fullWidth
                  />
                </Box>

                <Box
                  className={styles.componentType2}
                  style={{ marginTop: 16 }}
                >
                  <FormControl fullWidth required>
                    <InputLabel id={`issuing-state-label-${idx}`}>
                      Issuing State
                    </InputLabel>
                    <Select
                      labelId={`issuing-state-label-${idx}`}
                      name="issuingState"
                      label="Issuing State"
                      value={r.issuingState ?? ""} // <- never undefined
                      onChange={(e) => onAgencyRowChange(idx, e)}
                    >
                      {states.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth required>
                    <InputLabel id={`status-label-${idx}`}>Status</InputLabel>
                    <Select
                      labelId={`status-label-${idx}`}
                      name="status"
                      label="Status"
                      value={r.status ?? "Active"} // <- never undefined
                      onChange={(e) => onAgencyRowChange(idx, e)}
                    >
                      {ALLOWED_LICENSE_STATUS.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box
                  className={styles.componentType2}
                  style={{ marginTop: 16 }}
                >
                  <TextField
                    type="date"
                    label="Issued Date"
                    name="issuedDate"
                    value={r.issuedDate ?? ""} // <- never undefined
                    onChange={(e) => onAgencyRowChange(idx, e)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    type="date"
                    label="Expiry Date"
                    name="expiryDate"
                    value={r.expiryDate ?? ""} // <- never undefined
                    onChange={(e) => onAgencyRowChange(idx, e)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginTop: 16,
                  }}
                >
                  <input
                    id={`agency-lic-file-${idx}`}
                    type="file"
                    accept="image/*,application/pdf"
                    style={{ display: "none" }}
                    onChange={(e) => onAgencyRowFile(idx, e)}
                  />
                  <Button
                    variant="outlined"
                    onClick={() =>
                      document.getElementById(`agency-lic-file-${idx}`).click()
                    }
                  >
                    {r.file ? "Change Document" : "Upload Document"}
                  </Button>
                  {r.fileName && (
                    <Typography variant="caption">{r.fileName}</Typography>
                  )}
                </Box>
              </Box>
            ))}

            <Box>
              <Button variant="outlined" onClick={addAgencyRow}>
                + Add Another License
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAgencyAdd}>Cancel</Button>
          <Button variant="contained" onClick={submitAgencyLicensesUpload}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default DashProLicenses;
